import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { Network } from '@capacitor/network';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AlertService } from '@core/services/alert/alert.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { ApplicationOnCredit } from '@core/services/online-credit/online-credit.model';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';

import { LifecycleHookLogs } from '@shared/decorators/lifecycle-hook-logs/lifecycle-hook-logs.decorator';
import { FormFieldsListItem } from '@shared/models/modal-payment-form.model';
import { NetworkService } from '@shared/services/network.service';

import { Steps } from '../models/step.model';
import { CreateApplicationRequest } from '../store/store.model';
import { StoreService } from '../store/store.service';
import { ChoiceOfficeFormService } from './choice-office-form.service';

@LifecycleHookLogs()
@UntilDestroy()
@Component({
    selector: 'app-choice-office-step',
    templateUrl: 'choice-office-step.page.html',
    styleUrls: ['./choice-office-step.page.scss', '../new-credit.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChoiceOfficeStepPage implements OnInit, OnDestroy {
  title: string = 'Выберите офис';

  public formGroup: FormGroup;
  public formFields: FormFieldsListItem[];
  public loaded$: Observable<boolean>;
  public selectedOffice;
  public choiceOfficeSub = new Subject();

  private selectedCity: string;

  constructor(
    private readonly choiceOfficeFormService: ChoiceOfficeFormService,
    private readonly stepManagerService: StepManagerService,
    private readonly onlineCreditService: OnlineCreditService,
    private alertService: AlertService,
    private store: StoreService,
    private navigationService: NavigationService,
    private networkService: NetworkService
  ) {
    this.loaded$ = this.stepManagerService.stepLoaded$;
  }

  get isOnlineRegistration() {
    return this.store.value.isOnline;
  }

  get subtitle() {
    return this.isOnlineRegistration ? 'для обслуживания кредита' : 'для подачи заявки';
  }

  async ngOnInit() {
    this.choiceOfficeFormService
      .getChoiceOfficeForm()
      .pipe(untilDestroyed(this))
      .subscribe(
        (res) => {
          this.formGroup = res.form;
          this.formFields = res.fields;
          this.subscribeOnChanges();

          if (this.store.value.selectedOffice) {
            // @ts-ignore
            res.form.setValue({
              city: this.store.value.selectedOffice.office.addinfo || this.store.value.application.bankPlace,
              // @ts-ignore
              backId: this.store.value.selectedOffice.backId
            });
            this.changeBackOffice({
              city: this.store.value.selectedOffice.office.addinfo,
              backId: this.store.value.selectedOffice.backId
            });
          }
          if (this.store.value.application?.backId) {
            // @ts-ignore
            res.form.patchValue({
              city: this.store.value.application.bankPlace as any,
              backId: this.store.value.application.backId as any
            });
            this.changeBackOffice({
              city: this.store.value.application.bankPlace,
              backId: this.store.value.application.backId.toString()
            });
          }
          setTimeout(() => {
            this.stepManagerService.setStepLoaded(true);
          });
        },
        (error) => {
          const message = error.errorText || (error.error && error.error.errorText);
          this.alertService.showInfoAlert(message || error);
          void this.navigationService.setRoot('/main');
        }
      );
  }

  ionViewWillEnter() {}

  ngOnDestroy() {}

  ionViewWillLeave() {}

  async continueProcess() {
    await this.networkService.getStatus().then((status) => {
      if (!status) {
        this.alertService.showInfoAlert('Отсутствует подключение к интернету!');
        return;
      }
      this.stepManagerService.setStepLoaded(false);
      this.onlineCreditService
        .sendApplicationData({
          id: this.store.value.application.id,
          backId: this.formGroup.get('backId').value,
          isOnline: this.isOnlineRegistration
        })
        .pipe(untilDestroyed(this))
        .subscribe(
          (res: ApplicationOnCredit) => {
            this.store.updateApplication(res);
            this.isOnlineRegistration
              ? void this.navigationService.push('/new-product/credit/account-confirmation', { replaceUrl: true })
              : this.stepManagerService.showOfflineAlert();
            // this.stepManagerService.changeStep(Steps.accountConfirmation)
          },
          (error) => {
            const message = error.errorText || (error.error && error.error.errorText);
            this.alertService.showInfoAlert(message || error);
            this.stepManagerService.setStepLoaded(true);
          }
        );
    });

    // const request$ = this.store.createApplicationRequest$
    //   .pipe(
    //     map((request) => {
    //       return {
    //         ...request,
    //         backId: this.store.value.selectedOffice.backId,
    //         ...applicationExist && {
    //           ...this.store.value.application
    //         },
    //         isOnline: this.store.value.isOnline
    //       };
    //     })
    //   );
    // let action$ = applicationExist ? this.onlineCreditService.updateApplication : this.onlineCreditService.createApplication;
    // action$ = action$.bind(this.onlineCreditService);
    // request$.pipe(
    //   switchMap((request) => action$(request)),
    //   untilDestroyed(this)
    // ).subscribe((res) => {
    //     this.store.updateApplication(res);
    //     this.isOnlineRegistration ? this.stepManagerService.changeStep(Steps.accountConfirmation)
    //       : this.stepManagerService.showOfflineAlert()
    //   }
    // );
  }

  private subscribeOnChanges() {
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((res) => this.changeBackOffice(res));
  }

  private changeBackOffice({ city, backId }) {
    if (!city && backId) return this.formGroup.controls.backId.setValue(null);
    if (this.selectedCity !== city) backId = null;
    this.selectedOffice = this.choiceOfficeFormService.offices.find((o) => o.code === backId);
    this.store.updateSelectedOffice({ backId: backId, office: this.selectedOffice });
    this.selectedCity = city;

    const office = this.formFields.find((f) => f.controlName === 'backId');
    office.options =
      this.choiceOfficeFormService.officesOptions.filter((o) => o.returnValue === city) ||
      this.choiceOfficeFormService.officesOptions;
    office.fakeDsbl = city ? false : true;

    this.choiceOfficeSub.next(this.formFields);
  }

  goBack() {
    this.stepManagerService.setStepLoaded(false);
    void this.navigationService.push('/new-product/credit/personal-data', { replaceUrl: true });
    // this.stepManagerService.changeStep(Steps.personalData);
  }

  abort() {
    this.stepManagerService.showAbortAlert();
  }
}
